import React, { useEffect, useState, useMemo } from 'react';
import Swal from 'sweetalert2';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Container, Row, Col, Card, CardBody } from 'reactstrap';
import { Link } from 'react-router-dom';
import PreviewCardHeader from '../../../../components/common/PreviewCardHeader';
import TableContainer from '../../../../components/common/TableContainer';
import { FaRegSadCry,FaSlidersH } from 'react-icons/fa';
import Loader from '../../../../components/common/Loader';
import { getAllTargetAudience } from '../../../../slices/veloxity/targetAudience/getAllTargetAudience';
import showConfirmationAlert from '../../../../components/custom/confirmationAlert';
import { showErrorAlert, showSuccessAlert, showLoadingAlert } from '../../../../components/custom/sweetalertHelper';
import { deleteAudience } from '../../../../slices/veloxity/audience/DeleteRedujer';
import { unwrapResult } from '@reduxjs/toolkit';
import { getVeloxityCsvInfo } from '../../../../slices/veloxity/audience/CsvInfoReducer';
import { recalculateAudience } from '../../../../slices/veloxity/audience/RecalculateReducer';
import CreateTargetAudience from './CreateTargetAudience';
import DnaLookalikeModal from '../../../../components/modal/audience/DnaLookalikeModal';

const TargetAudience = () => {
  const dispatch = useDispatch();
  const { loading, payload: audienceData, error } = useSelector(state => state.targetAllAudience);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLookalikeModalOpen, setIsLookalikeModalOpen] = useState(false);
  const [selectedLookalikeItem, setSelectedLookalikeItem] = useState(null);

  const updateLoading = async (isLoading) => {
    setIsLoading(isLoading);
    if (isLoading === false) {
      await dispatch(getAllTargetAudience());
    }
  };

  useEffect(() => {
    dispatch(getAllTargetAudience());
  }, [dispatch]);

  const deleteAudienceClick = async (item) => {
    showConfirmationAlert("Are you sure you want to delete this audience?", async () => {
      try {
        const actionResult = await dispatch(deleteAudience({ audienceId: item.audienceId }));
        const { success, message } = unwrapResult(actionResult);
        if (success) {
          showSuccessAlert("Success!", message, "success");
          dispatch(getAllTargetAudience());
        } else {
          showErrorAlert("Error!", message, "error");
        }
      } catch (error) {
        showErrorAlert("Error!", error.message || "Deletion failed due to an unexpected error.", "error");
      }
    });
  };

  const sortedAudienceData = useMemo(() => {
    if (!audienceData || audienceData.length === 0) {
      return [];
    }
    return [...audienceData].sort((a, b) => new Date(a.updateDate) - new Date(b.updateDate));
  }, [audienceData]);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const toggleLookalikeModal = (item) => {
    setSelectedLookalikeItem(item);
    setIsLookalikeModalOpen(!isLookalikeModalOpen);
  };

  const onSuccess = () => {
    showSuccessAlert('Success', 'Target Audience created successfully.');
    dispatch(getAllTargetAudience())
  };

  const onError = () => {
    showErrorAlert('Error', 'Failed to create Target Audience.');
  };

  const fetchCsvInfo = async (audienceId) => {
    try {
      showLoadingAlert('Fetching Download...', 'Please wait while the CSV is being prepared.');

      const response = await dispatch(getVeloxityCsvInfo({ audienceId }));
      const csvInfo = unwrapResult(response);

      if (csvInfo && csvInfo.urls && csvInfo.urls["AD_ID"]) {
        const downloadUrl = csvInfo.urls["AD_ID"];
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', 'AudienceData.csv'); // Set the download file name
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showSuccessAlert("Download Started", "The CSV download has started.", "success");
      } else {
        Swal.close();
        showErrorAlert("Download Not Ready", "The CSV file is not yet ready for download. Please try again later.", "warning");
      }
    } catch (error) {
      console.error("Failed to fetch CSV info:", error);
      showErrorAlert("Download Failed", error.message || "Download failed due to an unexpected error.", "error");
    }
};


  const getStatusStyle = (status) => {
    switch (status) {
      case 'SUCCEEDED':
        return { color: 'success', text: 'Confirmed' };
      case 'CALCULATING':
        return { color: 'info', text: 'Calculating' };
      case 'SCHEDULED':
        return { color: 'warning', text: 'Scheduled' };
      case 'FAILED':
        return { color: 'danger', text: 'Failed' };
      default:
        return { color: 'secondary', text: 'Unknown' };
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Audience ID",
        accessor: "audienceCode",
        width: 150,
      },
      {
        Header: "Audience Name",
        accessor: "name",
        width: 200,
      },
      {
        Header: "Audience Size",
        accessor: "audienceSize",
        width: 150,
        Cell: ({ cell }) => {
          return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', fontWeight: 'bold', textAlign: 'center', margin: '0 auto', marginRight: '70%' }}>
              {cell.value ? cell.value.toLocaleString() : "0"}
            </div>
          );
        },
      },
      {
        Header: "Process",
        id: "process",
        accessor: "status",
        width: 150,
        Cell: ({ cell, row }) => {
          const statusStyle = getStatusStyle(cell.value);

          // Progress hesaplama
          let progress = 0;
          const createdAt = new Date(row.original.updateDate);
          const currentTime = new Date();
          const timeDifference = (currentTime - createdAt) / 60000; // Dakika farkı

          if (cell.value === 'FAILED') {
            progress = 0;
          } else if (cell.value === 'SCHEDULED' || cell.value === 'QUEUED') {
            progress = 30;
          } else if (cell.value === 'CALCULATING') {
            const minutesElapsed = timeDifference - 60;
            if (minutesElapsed > 0) {
              progress = Math.min(30 + (minutesElapsed * 3), 90);
            } else {
              progress = 30;
            }
          } else if (cell.value === 'SUCCEEDED') {
            progress = 100;
          }

          return (
            <div className="progress progress-sm">
              <div
                className={`progress-bar bg-${statusStyle.color}`}
                role="progressbar"
                style={{ width: `${progress}%` }}
                aria-valuenow={progress}
                aria-valuemin="0"
                aria-valuemax="100"
              ></div>
            </div>
          );
        },
      },
      {
        Header: "Status",
        id: "statusText",
        accessor: "status",
        width: 120,
        Cell: ({ cell }) => {
          const statusStyle = getStatusStyle(cell.value);
          return (
            <Link to="#" className={`link-${statusStyle.color}`}>
              {statusStyle.text}
            </Link>
          );
        },
      },
      {
        Header: "Creation Date",
        accessor: "updateDate",
        Cell: ({ cell }) => {
          const parsedDate = Date.parse(cell.value);
          if (isNaN(parsedDate)) {
            return "Invalid date";
          }
          const date = new Date(parsedDate);
          return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          });
        },
      },
      {
        Header: "Action",
        id: "action",
        Cell: ({ row }) => (
          <>
            <Link to="#">
              <i
                className={`ri-download-2-line fs-17 lh-1 align-middle ${!row.original.operationStatus.downloadableCsv ? 'text-muted' : ''}`}
                onClick={() => {
                  if (row.original.operationStatus.downloadableCsv) {
                    fetchCsvInfo(row.original.audienceId);
                  }
                }}
                style={{ pointerEvents: !row.original.operationStatus.downloadableCsv ? 'none' : 'auto', marginRight: '10px' }}
              ></i>
            </Link>
            <Link to="#">
              <i
                className={`ri-delete-bin-line fs-17 lh-1 align-middle ${!row.original.operationStatus.deleteActive ? 'text-muted' : 'text-danger'}`}
                onClick={() => {
                  if (row.original.operationStatus.deleteActive) {
                    deleteAudienceClick(row.original);
                  }
                }}
                style={{ pointerEvents: !row.original.operationStatus.deleteActive ? 'none' : 'auto', marginLeft: '10px', marginRight: '10px' }}
              ></i>
            </Link>
            <Link to="#">
              <i
                className={`ri-restart-line fs-17 lh-1 align-middle ${!row.original.operationStatus.reCalculateActive ? 'text-muted' : 'text-primary'}`}
                onClick={async () => {
                  if (row.original.operationStatus.reCalculateActive) {
                    showConfirmationAlert("Are you sure you want to recalculate this audience?", async () => {
                      try {
                        const actionResult = await dispatch(recalculateAudience(row.original.audienceId));
                        const { success, message } = unwrapResult(actionResult);
                        if (success) {
                          showSuccessAlert("Recalculation Successful!", message, "success");
                          dispatch(getAllTargetAudience());
                        } else {
                          showErrorAlert("Recalculation Failed!", message, "error");
                        }
                      } catch (error) {
                        showErrorAlert("Error!", error.message || "Recalculation failed due to an unexpected error.", "error");
                      }
                    });
                  }
                }}
                style={{ pointerEvents: !row.original.operationStatus.reCalculateActive ? 'none' : 'auto' }}
              ></i>
            </Link>
            <Link to="#">
              <FaSlidersH
                className={`fs-17 lh-1 align-middle ${!row.original.operationStatus.dnaLookalikeActive ? 'text-muted' : 'text-purple'}`}
                onClick={() => {
                  if (row.original.operationStatus.dnaLookalikeActive) {
                    toggleLookalikeModal(row.original);
                  }
                }}
                style={{ pointerEvents: !row.original.operationStatus.dnaLookalikeActive ? 'none' : 'auto', marginLeft: '10px' }}
              />
            </Link>

          </>
        ),
      },
    ],
    [dispatch]
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loader />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loader />
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Card
            className="p-3"
            style={{
              boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
              marginBottom: "30px",
              marginRight: "5%",
              width: "110%",
              height: "90%"
            }}
          >
            <Row>
              <Col className="d-flex justify-content-start">
                <Button color="primary" style={{ maxWidth: "190px" }} onClick={toggleModal}>
                  Create New
                </Button>
              </Col>
            </Row>
          </Card>
        </Row>
        <Row>
          <Card>
            <PreviewCardHeader title="TARGET AUDIENCE" />
            <CardBody>
              {error ? (
                <div className="text-center text-danger">
                  <p>Error: {error}</p>
                </div>
              ) : audienceData.length === 0 ? (
                <div className="text-center">
                  <FaRegSadCry size={50} color="gray" />
                  <h4 className="mt-3">No Target Audience Data Available</h4>
                  <p className="text-muted">It looks like you haven't created any Target Audience data yet. Start by creating your first Target Audience.</p>
                  <Button color="primary" onClick={toggleModal}>
                    Create New Target Audience
                  </Button>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <TableContainer
                    columns={columns}
                    data={sortedAudienceData}
                    isGlobalFilter={true}
                    SearchPlaceholder="Search Target Audience..."
                    tableClass="table-centered align-middle table-nowrap mb-0"
                    theadClass="text-muted table-light"
                    style={{ width: '100%' }}
                  />
                </div>
              )}
            </CardBody>
          </Card>
        </Row>
      </Container>

      {selectedLookalikeItem && (
        <DnaLookalikeModal
          isOpen={isLookalikeModalOpen}
          toggle={toggleLookalikeModal}
          audienceId={selectedLookalikeItem.audienceId}
          audience= {"target"}
        />
      )}


      <CreateTargetAudience
        isOpen={isModalOpen}
        updateLoading={updateLoading}
        toggle={toggleModal}
        onSuccess={onSuccess}
        onError={onError}
      />

    </div>
  );
};

export default TargetAudience;
